import { MASKED_SECRET, SETTINGS_SECTIONS } from '@glyph/protocol-ts';
import type { Settings, SettingsPatch, SettingsSection } from '@glyph/protocol-ts';

export function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a as object);
  const kb = Object.keys(b as object);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual((a as any)[k], (b as any)[k]));
}

/** Sections whose draft differs from the saved server copy. */
export function dirtySections(saved: Settings | undefined, draft: Settings | undefined): SettingsSection[] {
  if (!saved || !draft) return [];
  return SETTINGS_SECTIONS.filter((s) => !deepEqual(saved[s], draft[s]));
}

/**
 * Remove masked-secret sentinels from a section patch so the server never receives "••••••••" as a
 * new value (the old UI kept secrets empty in the input and only sent them when typed).
 */
export function stripMaskedSecrets<T extends Record<string, unknown>>(section: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(section)) {
    if (v === MASKED_SECRET) continue;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = stripMaskedSecrets(v as Record<string, unknown>);
      out[k] = inner;
    } else out[k] = v;
  }
  return out as Partial<T>;
}

/**
 * Build the PATCH body: only dirty sections, each shallow-diffed to the changed keys (nested objects
 * sent whole when any inner key changed), masked secrets dropped.
 */
export function buildSettingsPatch(saved: Settings, draft: Settings): SettingsPatch {
  const patch: SettingsPatch = {};
  for (const s of dirtySections(saved, draft)) {
    const a = saved[s] as unknown as Record<string, unknown>;
    const b = draft[s] as unknown as Record<string, unknown>;
    const changed: Record<string, unknown> = {};
    for (const k of Object.keys(b)) if (!deepEqual(a[k], b[k])) changed[k] = b[k];
    const clean = stripMaskedSecrets(changed);
    if (Object.keys(clean).length) (patch as any)[s] = clean;
  }
  return patch;
}

export const isMasked = (v: unknown) => v === MASKED_SECRET;
